import {get} from 'lodash';

import {ApolloError} from '../state/graphql/apolloWithAbort';

export function getErrMsgFromError(err: any): string | null {
  const apolloErrMsg = extractErrorMessageFromApolloError(err);
  if (apolloErrMsg) {
    return apolloErrMsg;
  }

  if (typeof err.message === `string` && err.message) {
    return err.message;
  }

  if (typeof err === `string` && err) {
    return err;
  }

  return null;
}

/**
 * Constructs a context that informs the error middleware to propagate errors
 * to the request issuer rather than intercepting them.
 */
export function propagateErrorsContext(): {propagateErrors: boolean} {
  return {
    propagateErrors: true,
  };
}

/**
 * Constructs a context that informs the error middleware to propagate errors
 * to the request issuer rather than intercepting them.
 * Including not forcing a relogin for 401 errors
 */
export function propagateErrorsNoLoginContext(): {
  propagateErrors: boolean;
  noLogin: boolean;
} {
  return {
    propagateErrors: true,
    noLogin: true,
  };
}

export function doNotRetryContext(): {doNotRetry: boolean} {
  return {
    doNotRetry: true,
  };
}

export function extractErrorMessageFromApolloError(
  err: unknown
): string | undefined {
  if (!(err instanceof ApolloError) && !(err instanceof Error)) {
    return;
  }

  const errMsg =
    get(err, 'graphQLErrors[0].message') ??
    get(err, 'networkError.result.errors[0].message') ??
    get(err, 'message');

  return errMsg;
}

// Structured code the backend tags run-filter rejections with (e.g. a non-RE2
// regex in run search). See services/gorilla/pkg/filtering.
export const INVALID_RUN_FILTER_CODE = 'INVALID_RUN_FILTER';

// Structured code the backend tags metric-search rejections with (a non-RE2
// regex in panel search). See services/gorilla/api/resolver/metric_search.go.
export const INVALID_METRIC_SEARCH_CODE = 'INVALID_METRIC_SEARCH';

type GraphQLErrorLike = {extensions?: {code?: string}; message?: string};

function extractTaggedErrorMessage(
  err: unknown,
  code: string
): string | undefined {
  // A rejection comes back as HTTP 400 (gerr.ErrInvalid), and Apollo
  // turns any non-2xx into a ServerError before the body's `errors` become
  // graphQLErrors — so the tagged error is under networkError.result.
  const candidates = [
    get(err, 'graphQLErrors'),
    get(err, 'networkError.result.errors'),
  ];
  for (const gqlErrors of candidates) {
    if (!Array.isArray(gqlErrors)) {
      continue;
    }
    const gqlErr = (gqlErrors as GraphQLErrorLike[]).find(
      e => e?.extensions?.code === code
    );
    if (gqlErr != null) {
      return gqlErr.message;
    }
  }
  return undefined;
}

export function extractRunFilterErrorMessage(err: unknown): string | undefined {
  return extractTaggedErrorMessage(err, INVALID_RUN_FILTER_CODE);
}

export function extractMetricSearchErrorMessage(
  err: unknown
): string | undefined {
  return extractTaggedErrorMessage(err, INVALID_METRIC_SEARCH_CODE);
}

export function extractStatusCodeFromApolloError(err: any): number | undefined {
  const statusCode =
    err?.networkError?.statusCode ?? err?.graphQLErrors?.[0]?.statusCode;

  return statusCode;
}
